import { persistentReducedStream, reducer } from '@ardoq/rxbeach';
import {
  DynamicReferenceCreationArmedPayload,
  DynamicReferenceCreationDroppedPayload,
  dynamicReferenceCreationArmed,
  dynamicReferenceCreationCreateSettled,
  dynamicReferenceCreationDisarmed,
  dynamicReferenceCreationDropped,
  dynamicReferenceCreationOptionSelected,
  dynamicReferenceCreationSourceMoved,
} from './dynamicReferenceCreationActions';
import { SourceBox } from './sourceAnchor';

/**
 * The phases are separate because the gesture has to behave differently in
 * each: while dragging it swallows presses so the drop does not change the
 * selection, and while picking it must not, or the picker's own clicks die.
 *
 * `creating` is why the gesture is one at a time. The picker offers a row as
 * pickable when the loaded graph holds no such reference, so until the one just
 * created is in there, a second gesture would offer it again and mean it.
 */
/**
 * `sourceBox` is filled by surfaces that publish where their nodes are, rather
 * than being asked per frame. `null` for the rest, whose boxes the gesture
 * resolves itself.
 */
type ArmedFields = DynamicReferenceCreationArmedPayload & {
  sourceBox: SourceBox | null;
};

export type DynamicReferenceCreationState =
  | { kind: 'idle' }
  | ({ kind: 'dragging' } & ArmedFields)
  | ({ kind: 'picking' } & ArmedFields & DynamicReferenceCreationDroppedPayload)
  | { kind: 'creating' };

export type PickingState = Extract<
  DynamicReferenceCreationState,
  { kind: 'picking' }
>;

/** The phases that are on screen, and so carry where the drag started. */
export type ArmedState = Extract<
  DynamicReferenceCreationState,
  { kind: 'dragging' } | { kind: 'picking' }
>;

export const isArmedState = (
  state: DynamicReferenceCreationState
): state is ArmedState => state.kind === 'dragging' || state.kind === 'picking';

const IDLE: DynamicReferenceCreationState = { kind: 'idle' };

const arm = (
  state: DynamicReferenceCreationState,
  payload: DynamicReferenceCreationArmedPayload
): DynamicReferenceCreationState =>
  state.kind === 'idle'
    ? { kind: 'dragging', ...payload, sourceBox: null }
    : state;

const moveSource = (
  state: DynamicReferenceCreationState,
  sourceBox: SourceBox
): DynamicReferenceCreationState =>
  isArmedState(state) ? { ...state, sourceBox } : state;

const drop = (
  state: DynamicReferenceCreationState,
  payload: DynamicReferenceCreationDroppedPayload
): DynamicReferenceCreationState =>
  state.kind === 'dragging' ? { ...state, ...payload, kind: 'picking' } : state;

const toCreating = (): DynamicReferenceCreationState => ({ kind: 'creating' });

const toIdle = (): DynamicReferenceCreationState => IDLE;

export const dynamicReferenceCreation$ =
  persistentReducedStream<DynamicReferenceCreationState>(
    'dynamicReferenceCreation$',
    IDLE,
    [
      reducer(dynamicReferenceCreationArmed, arm),
      reducer(dynamicReferenceCreationSourceMoved, moveSource),
      reducer(dynamicReferenceCreationDropped, drop),
      reducer(dynamicReferenceCreationOptionSelected, toCreating),
      reducer(dynamicReferenceCreationCreateSettled, toIdle),
      reducer(dynamicReferenceCreationDisarmed, toIdle),
    ]
  );
